import React, { ReactElement, useEffect, useRef, useState } from "react";
import {
  Alert,
  Form,
  Image,
  OverlayTrigger,
  PopoverBody,
  Popover,
  Spinner,
} from "react-bootstrap";
import FormStyles from "../Form.styles";
import ButtonStyles from "../../button/Button.styles";
import BUTTON from "../../button/Button.component";
import { Formik, FormikHelpers, FormikProps } from "formik";
import FormConstants from "../Form.constants";
import { IFormValues, IOutageFormProps } from "../Form.types";
import { useDispatch, useSelector } from "react-redux";
import { FormService } from "../Form.service";
import { ImageUploadService } from "../ImageUpload.service";
import { RootState } from "../../../state-management/root-reducer";
import FORM_CONFIRMATION from "./FormConfirmation.component";
import { OutageViewModel } from "../../../api/generated/models/outage-view-model";
import faker from "faker";
import { animateLoading, createLoadingTimeline } from "../Form.animations";
import NumberFormat, { NumberFormatValues } from "react-number-format";
import { useGTMDispatch } from "@elgorditosalsero/react-gtm-hook";
import BRACKET_TYPE_SELECTOR from "../bracket-type-selector/BracketTypeSelector.component";
import { ClickEvent } from "../../../shared/types/event.types";
import { GENERIC_ERROR_MESSAGE } from "../../../api/constants/error.constants";
import {
  clearImageData,
  setShowSafetyTooltip,
} from "../../../state-management/slices/outages/outages.slice";
import { ExclamationCircleFill, X } from "react-bootstrap-icons";
import PopupStyles from "../../map/widgets/Popup/Popup.styles";

const OUTAGE_FORM: React.FC<IOutageFormProps> = (props: IOutageFormProps) => {
  // props
  const { onClose } = props;

  // hooks
  const dispatch = useDispatch();
  const sendDataToGTM = useGTMDispatch();

  // services
  const formService: FormService = FormService.getInstance(
    dispatch,
    sendDataToGTM
  );
  const imageUploadService: ImageUploadService =
    ImageUploadService.getInstance(dispatch);

  // local state
  const [showSubmittedError, setShowSubmittedError] = useState(false);

  // App state
  const { submittedOutage, isLoading, imageUpload } = useSelector(
    (state: RootState) => state.outages
  );

  const { lookupCollection } = useSelector((state: RootState) => state.lookups);
  const { pinMode } = useSelector((state: RootState) => state.globalState.ui);
  const { outage } = submittedOutage;

  const validationSchema = formService.getValidationSchema();
  const formikReference: any = useRef(null);
  const { showSafetyTooltip } = imageUpload;

  let { renderOptions } = props;

  useEffect(() => {
    const loadingTimeline = createLoadingTimeline();

    if (formikReference.current) {
      formService.setFormikReference(formikReference);
    }

    if (submittedOutage.submitOutageError) {
      setShowSubmittedError(true);
      loadingTimeline.pause();
    } else {
      setShowSubmittedError(false);
      if (!loadingTimeline.began) {
        if (isLoading && !submittedOutage.submitOutageError) {
          animateLoading(loadingTimeline);
        }
      }
    }
  }, [formService, isLoading, submittedOutage.submitOutageError]);

  if (!renderOptions) {
    renderOptions = (outageReasons) => {
      return outageReasons.map((outageReason) => {
        return (
          <option
            key={`outageReason-${faker.datatype.uuid()}`}
            value={outageReason}
          >
            {outageReason}
          </option>
        );
      });
    };
  }

  return (
    <>
      {submittedOutage.isSubmitted && (
        <FORM_CONFIRMATION
          submittedOutage={outage}
          imageUploadState={imageUpload}
          onClose={(event: ClickEvent) => onClose(event)}
        />
      )}

      {!submittedOutage.isSubmitted && (
        <FormStyles.FormContainer className="form-container force-scroll">
          <Formik
            validationSchema={validationSchema}
            innerRef={formikReference}
            initialValues={FormConstants.InitialValues}
            onSubmit={(
              values: IFormValues,
              actions: FormikHelpers<IFormValues>
            ) => {
              const invalid =
                pinMode.enabled &&
                (!values.outageFormBracketType ||
                  values.outageFormBracketType === "");

              if (invalid) return;
              formService.submitOutageReport(values, actions);
            }}
          >
            {({
              handleSubmit,
              handleChange,
              handleBlur,
              setFieldValue,
              values,
              touched,
              isValid,
              errors,
              resetForm,
            }) => (
              <Form id="OutageForm" noValidate onSubmit={handleSubmit}>
                <FormStyles.FormGroup className="form-group">
                  {/* -------------------------------- */}
                  {/* FORM HEADING ------------------- */}
                  {/* -------------------------------- */}
                  <FormStyles.FormHeading className="form-heading">
                    <h1>Let&#39;s see what the problem is.</h1>
                    <p>
                      Please provide as much information as you can to assist us
                      with your streetlight outage report.
                    </p>
                  </FormStyles.FormHeading>

                  {/* ------------------------------------- */}
                  {/* BRACKET TYPE SELECTOR (Pin Mode Only) */}
                  {/* ------------------------------------- */}
                  {pinMode.enabled && (
                    <FormStyles.FormGroup className="form-group">
                      <FormStyles.FormLabel className="outage-form-dropdown-label">
                        What style of light is it?
                      </FormStyles.FormLabel>
                      <FormStyles.FormControl
                        required
                        as={BRACKET_TYPE_SELECTOR}
                        title="outageFormBracketType"
                        name="outageFormBracketType"
                        value={values.outageFormBracketType}
                        onChange={(event: any) => {
                          setFieldValue(
                            "outageFormBracketType",
                            event.newValue
                          );
                        }}
                      />
                      <Form.Control.Feedback
                        type="invalid"
                        style={
                          pinMode.enabled
                            ? {
                                display: "block",
                              }
                            : { display: "none" }
                        }
                      >
                        {errors?.outageFormBracketType}
                      </Form.Control.Feedback>
                    </FormStyles.FormGroup>
                  )}

                  {/* --------------------------------- */}
                  {/* OUTAGE REASON ------------------- */}
                  {/* --------------------------------- */}
                  <FormStyles.FormLabel className="outage-form-dropdown-label">
                    What&#39;s wrong with the light?
                  </FormStyles.FormLabel>
                  <FormStyles.FormControl
                    required
                    title="outageReasonDropdown"
                    name="outageFormDropdown"
                    className={
                      touched.outageFormDropdown &&
                      (errors.outageFormDropdown ||
                        values.outageFormDropdown === "Select")
                        ? "outage-form-dropdown is-invalid"
                        : "outage-form-dropdown"
                    }
                    as="select"
                    placeholder="Select"
                    value={values.outageFormDropdown}
                    isValid={
                      touched.outageFormDropdown &&
                      !errors.outageFormDropdown &&
                      values.outageFormDropdown !== "Select"
                    }
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    <option value="Select" className="select-placeholder">
                      Select
                    </option>
                    {renderOptions &&
                      lookupCollection &&
                      lookupCollection.outageReasons &&
                      renderOptions(lookupCollection.outageReasons)}
                  </FormStyles.FormControl>
                  <Form.Control.Feedback type="invalid">
                    {errors?.outageFormDropdown}
                  </Form.Control.Feedback>
                </FormStyles.FormGroup>

                {/* --------------------------------- */}
                {/* COMMENTS ------------------------ */}
                {/* --------------------------------- */}
                <FormStyles.FormGroup
                  className="form-group"
                  controlId="outageFormCommentsCtrl"
                >
                  <FormStyles.FormLabel className="form-label">
                    Is there anything else we should know?
                  </FormStyles.FormLabel>
                  <FormStyles.FormLabel className="form-label optional">
                    (optional)
                  </FormStyles.FormLabel>
                  <FormStyles.FormControl
                    as="textarea"
                    name="outageFormComments"
                    className={
                      touched.outageFormComments && errors.outageFormComments
                        ? "form-text-area is-invalid"
                        : "form-text-area"
                    }
                    placeholder="Ex. The light started flickering at midnight"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isValid={
                      touched.outageFormComments && !errors.outageFormComments
                    }
                    value={values.outageFormComments}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors?.outageFormComments}
                  </Form.Control.Feedback>
                  <FormStyles.FormLabel className="form-counter">
                    ({values.outageFormComments.length}/190)
                  </FormStyles.FormLabel>
                </FormStyles.FormGroup>

                {/* --------------------------------- */}
                {/* FILE UPLOAD --------------------- */}
                {/* --------------------------------- */}
                <FormStyles.FormGroup className="form-group file-upload-form-group">
                  <FormStyles.FormLabel className="form-label">
                    Upload a photo of the light
                  </FormStyles.FormLabel>
                  <p className="upload-message">
                    Providing a picture of the streetlight and its surroundings
                    can help us locate and service it quicker.
                  </p>

                  <OverlayTrigger
                    trigger="click"
                    placement="auto"
                    show={showSafetyTooltip}
                    overlay={
                      <Popover
                        id="SafetyWarningPopover"
                        className="safety-warning-popover"
                      >
                        <PopoverBody className="popover-content-container">
                          <div className="popover-content">
                            Make sure to obey all traffic laws and{" "}
                            <b>maintain a safe distance</b> (at least 25 feet)
                            when taking a photo, especially if there’s damage,
                            wires down or exposed, etc.
                          </div>
                          <div className="close-button-container">
                            <PopupStyles.CloseButton
                              onClick={(event: ClickEvent) => {
                                dispatch(setShowSafetyTooltip(false));
                              }}
                              title="Close Button"
                            >
                              <X height="24" width="24" />
                            </PopupStyles.CloseButton>
                          </div>
                        </PopoverBody>
                      </Popover>
                    }
                    onToggle={(nextShow: boolean) => {
                      dispatch(setShowSafetyTooltip(nextShow));
                    }}
                  >
                    <ButtonStyles.LinkButton className="link-button">
                      <ExclamationCircleFill />
                      Safety Warning
                    </ButtonStyles.LinkButton>
                  </OverlayTrigger>

                  {imageUpload.imageProcessing && (
                    <div className="processing-image-container">
                      <Spinner animation="border" variant="primary" />
                    </div>
                  )}

                  {!imageUpload.imgDataUrl && !imageUpload.imageProcessing && (
                    <div className="file-upload-control">
                      <FormStyles.FormLabel
                        htmlFor="outageFormFileUpload"
                        className="form-label file-upload-label"
                      >
                        Choose Photo
                      </FormStyles.FormLabel>
                      <FormStyles.FormControl
                        id="outageFormFileUpload"
                        className="form-control"
                        data-testid="file-upload-control"
                        type="file"
                        name="outageFormFile"
                        accept=".jpg, .jpeg, .png, .heic"
                        onChange={(event: React.ChangeEvent<any>) => {
                          imageUploadService.processImageUpload(event);
                          setFieldValue(
                            "outageFormFile",
                            event.target.files[0]
                          );
                          sendDataToGTM({
                            event: "eventTracking",
                            category: "Outage",
                            action: "Street Light",
                            label: "Image Selected - Outage Form",
                          });
                        }}
                      />
                      <span className="file-upload-types">
                        Accepted file formats: JPG, PNG, HEIC
                      </span>
                    </div>
                  )}

                  {imageUpload.imgDataUrl && !imageUpload.imageProcessing && (
                    <div className="image-container">
                      <Image src={imageUpload.imgDataUrl as any} />
                      <div className="image-controls">
                        <div>
                          <FormStyles.FormLabel
                            htmlFor="FileUpload"
                            className="form-label file-upload-label"
                          >
                            Replace Photo
                          </FormStyles.FormLabel>
                          <FormStyles.FormControl
                            id="FileUpload"
                            className="form-control"
                            data-testid="replace-file-control"
                            type="file"
                            name="outageFormFile"
                            accept=".jpg, .jpeg, .png, .heic"
                            onChange={(event: React.ChangeEvent<any>) => {
                              imageUploadService.processImageUpload(event);
                              setFieldValue(
                                "outageFormFile",
                                event.target.files[0]
                              );
                            }}
                          />
                        </div>

                        <BUTTON
                          variant="secondary"
                          className="remove-image"
                          onClick={(event: ClickEvent) => {
                            event.preventDefault();
                            dispatch(clearImageData());
                          }}
                        >
                          Remove Photo
                        </BUTTON>
                      </div>
                    </div>
                  )}
                </FormStyles.FormGroup>

                {/* --------------------------------- */}
                {/* USERS NAME ---------------------- */}
                {/* --------------------------------- */}
                <FormStyles.FormGroup
                  className="form-group"
                  controlId="outageFormNameCtrl"
                >
                  <FormStyles.FormHeading className="form-heading">
                    <h1>One last thing!</h1>
                    <p>
                      We require your contact information should we have
                      questions on your outage. If we&#39;re unable to reach
                      you, your order may be cancelled.
                    </p>
                  </FormStyles.FormHeading>
                  <FormStyles.FormLabel className="form-label">
                    Name
                  </FormStyles.FormLabel>
                  <FormStyles.FormControl
                    required
                    type="text"
                    name="outageFormName"
                    className={
                      touched.outageFormName && errors.outageFormName
                        ? "is-invalid"
                        : ""
                    }
                    placeholder="First Last"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isValid={touched.outageFormName && !errors.outageFormName}
                    value={values.outageFormName}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors?.outageFormName}
                  </Form.Control.Feedback>
                </FormStyles.FormGroup>

                {/* --------------------------------- */}
                {/* USERS PHONE NUMBER -------------- */}
                {/* --------------------------------- */}
                <FormStyles.FormGroup
                  className="form-group"
                  controlId="outageFormPhoneCtrl"
                >
                  <FormStyles.FormLabel className="form-label">
                    Phone Number
                  </FormStyles.FormLabel>
                  <NumberFormat
                    allowEmptyFormatting
                    required
                    mask="_"
                    type="tel"
                    name="outageFormPhone"
                    customInput={FormStyles.FormControl}
                    placeholder="555-555-5555"
                    format="(###) ###-####"
                    className={
                      touched.outageFormPhone && errors.outageFormPhone
                        ? "form-phone-number is-invalid"
                        : "form-phone-number"
                    }
                    value={values.outageFormPhone}
                    onValueChange={(event: NumberFormatValues) => {
                      setFieldValue("outageFormPhone", event.floatValue);
                    }}
                    onBlur={handleBlur}
                    isValid={touched.outageFormPhone && !errors.outageFormPhone}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.outageFormPhone}
                  </Form.Control.Feedback>
                </FormStyles.FormGroup>

                {/* --------------------------------- */}
                {/* USERS EMAIL ADDRESS ------------- */}
                {/* --------------------------------- */}
                <FormStyles.FormGroup
                  className="form-group"
                  controlId="outageFormEmailCtrl"
                >
                  <FormStyles.FormLabel className="form-label">
                    Email address
                  </FormStyles.FormLabel>
                  <FormStyles.FormControl
                    required
                    type="email"
                    name="outageFormEmail"
                    className={
                      touched.outageFormEmail && errors.outageFormEmail
                        ? "form-email is-invalid"
                        : "form-email"
                    }
                    placeholder="user@email.com"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isValid={touched.outageFormEmail && !errors.outageFormEmail}
                    value={values.outageFormEmail}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.outageFormEmail}
                  </Form.Control.Feedback>
                </FormStyles.FormGroup>

                {/* --------------------------------- */}
                {/* SUBMIT BUTTON AND ALERTS -------- */}
                {/* --------------------------------- */}
                <FormStyles.FormSubmitContainer className="form-submit-container">
                  {showSubmittedError && (
                    <Alert
                      variant="danger"
                      onClose={() => setShowSubmittedError(false)}
                      dismissible
                    >
                      <Alert.Heading>Submission Failed</Alert.Heading>
                      <p>{GENERIC_ERROR_MESSAGE}</p>
                    </Alert>
                  )}
                  <BUTTON
                    className="submit-outage-button"
                    variant="primary"
                    type="submit"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <div className="loading-spinner">
                        <Spinner animation="border" variant="light" />
                        <span className="loading-message">
                          One Moment Please...
                        </span>
                      </div>
                    ) : (
                      "Submit Outage Report"
                    )}
                  </BUTTON>

                  <ButtonStyles.ButtonContainer className="button-container">
                    <ButtonStyles.LinkButton
                      className="link-button"
                      title="Back to Outage Map"
                      type="button"
                      disabled={isLoading}
                      onClick={(event) => {
                        sendDataToGTM({
                          event: "eventTracking",
                          category: "Outage",
                          action: "Street Light",
                          label: "Back to map clicked from outage form",
                        });
                        setShowSafetyTooltip(false);
                        onClose(event);
                      }}
                    >
                      Back to Outage Map
                    </ButtonStyles.LinkButton>
                  </ButtonStyles.ButtonContainer>
                </FormStyles.FormSubmitContainer>
              </Form>
            )}
          </Formik>
        </FormStyles.FormContainer>
      )}
    </>
  );
};

export default OUTAGE_FORM;
